import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Grid,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Mail as MailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ExcelJS from 'exceljs';
import UserRestrictionsDialog from '../../components/UserRestrictionsDialog';
import { hasPermissionFromToken } from '../../utils/rolePermissions';

interface User {
  userId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  businessName: string;
  locationName: string;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  drivingLicense?: {
    licenseNumber: string;
    address: string;
  };
  lastInvitedAt?: string; // From user_invitations table's created_at field
}

interface UserDetails {
  userId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  businessName: string;
  locationName: string;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  teams: Array<{
    teamId: string;
    teamName: string;
    description: string;
    teamLeaderId: string;
    teamLeaderFirstName: string;
    teamLeaderLastName: string;
    teamLeaderEmail: string;
    teamLeaders?: Array<{
      userId: string;
      firstName: string;
      lastName: string;
      email: string;
    }>;
  }>;
  jobTags: Array<{
    jobTagId: string;
    tagName: string;
    description: string;
    assignedAt: string;
  }>;
  drivingLicense?: {
    licenseNumber: string;
    address: string;
    lastCheck: string;
    status: string;
    points: number;
    restrictions: any;
  };
}

interface Role {
  roleId: string;
  roleName: string;
}

interface JobTag {
  job_tag_id: string;
  name: string;
}

interface Team {
  group_id: string;
  name: string;
}

interface BulkImportUser {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  drivingLicense?: {
    licenseNumber: string;
    address: string;
  };
  jobTags?: string[];
  teams?: string[];
}

interface BulkImportResponse {
  success: boolean;
  results: Array<{
    email: string;
    success: boolean;
    userId: string;
  }>;
  errors?: Array<{
    email: string;
    error: string;
  }>;
  error?: string;
}

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
  roles: Role[];
  jobTags: JobTag[];
  teams: Team[];
}

const BulkImportDialog = ({
  open,
  onClose,
  onSuccess,
  roles,
  jobTags,
  teams
}: BulkImportDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BulkImportUser[]>([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [importedUsers, setImportedUsers] = useState<BulkImportUser[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setPreview([]);
      setShowInviteDialog(false);
      setImportedUsers([]);
      setSuccessMessage(null);
      setInviteSuccess(null);
    }
  }, [open]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    try {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setError(null);
        
        const workbook = new ExcelJS.Workbook();
        const buffer = await selectedFile.arrayBuffer();
        await workbook.xlsx.load(buffer);
        
        const worksheet = workbook.getWorksheet(1);
        if (!worksheet) {
          throw new Error('No worksheet found in the Excel file');
        }

        // Get headers from first row
        const headers = worksheet.getRow(1).values as string[];
        if (!headers) {
          throw new Error('No headers found in the Excel file');
        }

        // Validate headers
        const requiredHeaders = ['Email*', 'First Name*', 'Last Name*', 'Role*'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
        }

        // Parse data starting from second row
        const users: BulkImportUser[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header row

          const user: BulkImportUser = {
            email: row.getCell(headers.indexOf('Email*')).value?.toString() || '',
            firstName: row.getCell(headers.indexOf('First Name*')).value?.toString() || '',
            lastName: row.getCell(headers.indexOf('Last Name*')).value?.toString() || '',
            role: row.getCell(headers.indexOf('Role*')).value?.toString() || ''
          };

          // Optional fields
          const licenseNumberCell = row.getCell(headers.indexOf('License Number'));
          const addressCell = row.getCell(headers.indexOf('Address'));
          if (licenseNumberCell.value || addressCell.value) {
            user.drivingLicense = {
              licenseNumber: licenseNumberCell.value?.toString() || '',
              address: addressCell.value?.toString() || ''
            };
          }

          const jobTagsCell = row.getCell(headers.indexOf('Tags'));
          if (jobTagsCell.value) {
            user.jobTags = jobTagsCell.value.toString()
              .split(';')
              .map(tag => tag.trim())
              .filter(tag => tag.length > 0)
              .slice(0, 5);
          }

          const teamsCell = row.getCell(headers.indexOf('Teams'));
          if (teamsCell.value) {
            user.teams = teamsCell.value.toString()
              .split(';')
              .map(team => team.trim())
              .filter(team => team.length > 0)
              .slice(0, 5);
          }

          // Only add user if they have the required fields
          if (user.email && user.firstName && user.lastName && user.role) {
            users.push(user);
          }
        });

        setPreview(users);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to parse Excel file');
      setPreview([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview.length) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Sending bulk import request with users:', preview);

      const response = await fetch(`${API_BASE_URL}/users/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ users: preview }),
      });

      console.log('Response status:', response.status);
      const data: BulkImportResponse = await response.json();
      console.log('Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Failed to import users');
      }

      // Show success message with results
      const successCount = data.results?.filter(r => r.success).length || 0;
      const errorCount = data.errors?.length || 0;
      
      console.log('Import results:', { 
        successCount, 
        errorCount, 
        results: data.results, 
        errors: data.errors,
        rawData: data 
      });
      
      if (successCount === 0 && errorCount === 0) {
        throw new Error('No users were imported and no errors were reported');
      }

      // Create detailed success message
      let message = `Successfully imported ${successCount} user${successCount !== 1 ? 's' : ''}`;
      
      if (errorCount > 0) {
        message += `\n\nFailed to import ${errorCount} user${errorCount !== 1 ? 's' : ''}:`;
        data.errors?.forEach((error, index) => {
          message += `\n${index + 1}. ${error.email}: ${error.error}`;
        });
      }

      setSuccessMessage(message);

      // Only show invite dialog if there were successful imports
      if (successCount > 0) {
        // Get the successfully imported users from the preview
        const successfulUsers = preview.filter(user => 
          data.results?.some(result => result.email === user.email && result.success)
        );
        console.log('Successfully imported users:', successfulUsers);
        setImportedUsers(successfulUsers);
        setShowInviteDialog(true);
      }
    } catch (error) {
      console.error('Import error:', error);
      setError(error instanceof Error ? error.message : 'Failed to import users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    try {
      setSendingInvites(true);
      setInviteSuccess(null);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const invitationsToSend = importedUsers.map(user => ({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      }));

      const response = await fetch(`${API_BASE_URL}/invitations/send-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invitations: invitationsToSend
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to send invitations');
      }

      const successMsg = `Successfully sent ${importedUsers.length} invitation${importedUsers.length !== 1 ? 's' : ''}`;
      setInviteSuccess(successMsg);
      
      setTimeout(() => {
        setShowInviteDialog(false);
        onSuccess(successMsg);
      }, 2000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send invitations');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Create Excel workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('User Import Template');

    // Define columns
    worksheet.columns = [
      { header: 'Email*', key: 'email', width: 30 },
      { header: 'First Name*', key: 'firstName', width: 20 },
      { header: 'Last Name*', key: 'lastName', width: 20 },
      { header: 'Role*', key: 'role', width: 15 },
      { header: 'License Number', key: 'licenseNumber', width: 20 },
      { header: 'Address', key: 'address', width: 40 },
      { header: 'Tags', key: 'jobTags', width: 30 },
      { header: 'Teams', key: 'teams', width: 30 }
    ];

    // Get example role from available roles
    const exampleRole = roles.length > 0 ? roles[0].roleName : 'User';

    // Add example data
    worksheet.addRow({
      email: 'john.doe@example.com',
      firstName: 'John',
      lastName: 'Doe',
      role: exampleRole,
      licenseNumber: 'ABCDE123456FG789',
      address: '123 Main St, City, Country',
      jobTags: 'Tag1;Tag2;Tag3',
      teams: 'Team1;Team2'
    });

    // Style the header row
    worksheet.getRow(1).font = { bold: true };

    // Generate and download the Excel file
    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'user_import_template.xlsx');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Import Users</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <input
              accept=".xlsx"
              style={{ display: 'none' }}
              id="bulk-import-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="bulk-import-file">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
              >
                Select Excel File
              </Button>
            </label>
            <Button
              variant="outlined"
              onClick={handleDownloadTemplate}
              startIcon={<DownloadIcon />}
            >
              Download Template
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
              {successMessage}
            </Alert>
          )}

          {preview.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Preview ({preview.length} users)
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Driving License</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell>Teams</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell>
                          {user.drivingLicense ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell>
                          {user.jobTags?.length || 0} tags
                        </TableCell>
                        <TableCell>
                          {user.teams?.length || 0} teams
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!preview.length || loading}
          >
            {loading ? 'Importing...' : 'Import Users'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={showInviteDialog} 
        onClose={() => {
          setShowInviteDialog(false);
          onSuccess();
        }}
      >
        <DialogTitle>Send Invitations</DialogTitle>
        <DialogContent>
          {inviteSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {inviteSuccess}
            </Alert>
          ) : (
            <Typography>
              Would you like to send invitations to all imported users now?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowInviteDialog(false);
              onSuccess();
            }}
            disabled={sendingInvites}
          >
            Skip
          </Button>
          <Button 
            onClick={handleInvite} 
            variant="contained"
            disabled={sendingInvites}
          >
            {sendingInvites ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Sending Invitations...
              </>
            ) : (
              'Send Invitations'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const getUserRoleFromToken = (): string | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role || null;
  } catch (error) {
    console.error('Error decoding token for role:', error);
    return null;
  }
};

const getIsMultiBusinessManagerFromToken = (): boolean => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.multiBusinessManager === true;
  } catch {
    return false;
  }
};

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState<UserDetails | null>(null);
  const [userDetailsDialogOpen, setUserDetailsDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({ email: '', firstName: '', lastName: '' });
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkInviteDialogOpen, setBulkInviteDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [bulkInviteSuccess, setBulkInviteSuccess] = useState<string | null>(null);
  const [bulkInviteError, setBulkInviteError] = useState<string | null>(null);
  const [loadingBulkInvite, setLoadingBulkInvite] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [restrictionsDialogOpen, setRestrictionsDialogOpen] = useState(false);
  const [restrictionsTarget, setRestrictionsTarget] = useState<{ userId: string; userName: string } | null>(null);

  const handleAuthError = (error: any) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      setError('Session expired. Please log in again.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else if (error.response?.status === 403) {
      setError('You do not have permission to perform this action.');
    } else {
      setError(error.response?.data?.error || 'An error occurred');
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Wrap fetchAvailableUsers in useCallback to stabilize its reference
  const fetchAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/invitations/available-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAvailableUsers(response.data.users);
      }
    } catch (error: any) {
      console.error('Error fetching available users:', error);
      setError(error.response?.data?.message || 'Failed to fetch available users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Effect to load available users when bulk invite dialog opens
  useEffect(() => {
    if (bulkInviteDialogOpen) {
      fetchAvailableUsers();
    }
  }, [bulkInviteDialogOpen, fetchAvailableUsers]);

  const filteredMainUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleEditUser = (userId: string) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(users.filter(user => user.userId !== userId));
    } catch (error: any) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserDetails = async (userId: string) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedUserDetails(response.data);
      setUserDetailsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      handleAuthError(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreateUser = () => {
    navigate('/admin/create-user');
  };

  const handleInviteUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      console.log('Sending invitation request:', inviteFormData);

      const response = await axios.post(`${API_BASE_URL}/invitations/send`, inviteFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Invitation response:', response.data);

      if (response.data.success) {
        setInviteSuccess('Invitation sent successfully');
        setInviteDialogOpen(false);
        setInviteFormData({ email: '', firstName: '', lastName: '' });
        setTimeout(() => setInviteSuccess(null), 3000);
        
        // Refresh the available users list
        fetchAvailableUsers();
      } else {
        setInviteError(response.data.message || 'Failed to send invitation');
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        setInviteError(error.response.data.message || 'Failed to send invitation');
      } else if (error.request) {
        console.error('Error request:', error.request);
        setInviteError('No response received from server');
      } else {
        console.error('Error message:', error.message);
        setInviteError('An error occurred while sending the invitation');
      }
    }
  };

  const handleResendInvitation = async (user: User) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const invitationData = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      };

      const response = await axios.post(`${API_BASE_URL}/invitations/send`, invitationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccessMessage(`Invitation resent successfully to ${user.email}`);
        setTimeout(() => setSuccessMessage(null), 5000);
        // Refresh users to update lastInvitedAt
        fetchUsers();
      } else {
        setError(response.data.message || 'Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      if (error.response) {
        setError(error.response.data.message || 'Failed to resend invitation');
      } else {
        setError('An error occurred while resending the invitation');
      }
    }
  };

  const handleBulkInviteSelected = async () => {
    try {
      setLoadingBulkInvite(true);
      setBulkInviteError(null);
      setBulkInviteSuccess(null);
      
      if (selectedUsers.length === 0) {
        setBulkInviteError('Please select at least one user to invite');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setBulkInviteError('No authentication token found');
        return;
      }

      // Map selected user IDs to invitation payload expected by backend
      const selectedUserDetails = availableUsers.filter(u => selectedUsers.includes(u.userId));
      const invitations = selectedUserDetails.map(u => ({
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName
      }));

      const response = await axios.post(
        `${API_BASE_URL}/invitations/send-bulk`,
        { invitations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const sentCount = Array.isArray(response.data.results)
          ? response.data.results.filter((r: any) => r && r.success).length
          : invitations.length;
        setBulkInviteSuccess(`Successfully sent ${sentCount} invitation${sentCount !== 1 ? 's' : ''}`);
        // Clear selections
        setSelectedUsers([]);
        // Refresh available users
        fetchAvailableUsers();
        // Close dialog after delay
        setTimeout(() => {
          setBulkInviteDialogOpen(false);
          setBulkInviteSuccess(null);
        }, 2000);
      } else {
        setBulkInviteError(response.data.message || 'Failed to send invitations');
      }
    } catch (error: any) {
      console.error('Error sending bulk invitations:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        setBulkInviteError(error.response.data.message || 'Failed to send invitations');
      } else {
        setBulkInviteError('An error occurred while sending invitations');
      }
    } finally {
      setLoadingBulkInvite(false);
    }
  };

  // Re-invite restriction removed: allow inviting at any time

  const formatLastInvitedTime = (lastInvitedAt: string | undefined) => {
    if (!lastInvitedAt) return 'Never';
    
    const lastInvited = new Date(lastInvitedAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastInvited.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.ceil(diffTime / (1000 * 60));
        return `${diffMinutes} minute(s) ago`;
      }
      return `${diffHours} hour(s) ago`;
    }
    return `${diffDays} day(s) ago`;
  };

  const fetchRolesAndTags = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const [rolesRes, jobTagsRes, teamsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/roles`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/job-tags`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/teams`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!rolesRes.ok || !jobTagsRes.ok || !teamsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [rolesData, jobTagsData, teamsData] = await Promise.all([
        rolesRes.json(),
        jobTagsRes.json(),
        teamsRes.json()
      ]);

      setRoles(rolesData);
      // Only show tags that are assignable to users (scope 'both' or 'user')
      setJobTags((jobTagsData as any[]).filter((t: any) => !t.tag_scope || t.tag_scope !== 'asset'));
      setTeams(teamsData);
    } catch (error) {
      console.error('Error fetching roles and tags:', error);
    }
  };

  useEffect(() => {
    fetchRolesAndTags();
  }, []);

  useEffect(() => {
    setCurrentUserRole(getUserRoleFromToken());
  }, []);

  const canManageUsers = hasPermissionFromToken('manage_users');
  const isAdminOrSuperAdmin =
    currentUserRole === 'Admin' ||
    currentUserRole === 'Super Admin' ||
    currentUserRole === 'Owner' ||
    currentUserRole === 'Multi Business Manager' ||
    getIsMultiBusinessManagerFromToken();

  const handleOpenRestrictions = (userId: string, firstName: string, lastName: string) => {
    setRestrictionsTarget({ userId, userName: `${firstName} ${lastName}` });
    setRestrictionsDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ maxWidth: { sm: 300 } }}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 150 } }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {Array.from(new Set(roles.map(r => r.roleName))).map((roleName) => (
              <MenuItem key={roleName} value={roleName}>
                {roleName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 150 } }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Tooltip
          title={!canManageUsers ? 'You do not have permission to create users' : ''}
          disableHoverListener={canManageUsers}
        >
          <span style={{ display: 'inline-flex' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (!canManageUsers) return;
                handleCreateUser();
              }}
              disabled={!canManageUsers}
              sx={{ cursor: !canManageUsers ? 'not-allowed' : 'pointer' }}
            >
              Create User
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite User
        </Button>
        <Button
          variant="outlined"
          onClick={() => setBulkInviteDialogOpen(true)}
        >
          Bulk Invite
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setBulkImportDialogOpen(true)}
        >
          Bulk Import
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredMainUsers.map((user) => (
            <Paper key={user.userId} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {`${user.firstName} ${user.lastName}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  {!user.lastLoginAt && (
                    <Tooltip title="Resend invitation - User is pending">
                      <IconButton
                        color="warning"
                        onClick={() => handleResendInvitation(user)}
                        size="small"
                      >
                        <MailIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isAdminOrSuperAdmin && (
                    <Tooltip title="Manage restrictions">
                      <IconButton
                        color="default"
                        onClick={() => handleOpenRestrictions(user.userId, user.firstName, user.lastName)}
                        size="small"
                      >
                        <LockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    color="primary"
                    onClick={() => handleViewUserDetails(user.userId)}
                    size="small"
                  >
                    <ViewIcon />
                  </IconButton>
                  <Tooltip
                    title={!canManageUsers ? 'You do not have permission to edit users' : 'Edit user'}
                    disableHoverListener={canManageUsers}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (!canManageUsers) return;
                          handleEditUser(user.userId);
                        }}
                        size="small"
                        disabled={!canManageUsers}
                        sx={{ cursor: !canManageUsers ? 'not-allowed' : 'pointer' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteUser(user.userId)}
                    disabled={!user.isActive}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Chip
                  label={user.role}
                  color={user.role === 'Admin' ? 'primary' : 'default'}
                  size="small"
                />
                <Chip
                  label={user.isActive ? 'Active' : 'Inactive'}
                  color={user.isActive ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Last Login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
              </Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMainUsers.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={user.role === 'Admin' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.businessName}</TableCell>
                  <TableCell>{user.locationName}</TableCell>
                  <TableCell>
                    {!user.isActive ? (
                      <Tooltip title="User account is deactivated">
                        <span>
                          <Chip
                            label="Inactive"
                            color="error"
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    ) : !user.lastLoginAt ? (
                      <Tooltip title={
                        user.lastInvitedAt ? 
                          `Invited ${formatLastInvitedTime(user.lastInvitedAt)}` : 
                          'Created but not invited yet'
                      }>
                        <span>
                          <Chip
                            label="Pending"
                            color="warning"
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Last login: ${new Date(user.lastLoginAt).toLocaleString()}`}>
                        <span>
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {!user.lastLoginAt && (
                        <Tooltip title="Resend invitation - User is pending">
                          <IconButton
                            color="warning"
                            onClick={() => handleResendInvitation(user)}
                            size="small"
                          >
                            <MailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdminOrSuperAdmin && (
                        <Tooltip title="Manage restrictions">
                          <IconButton
                            color="default"
                            onClick={() => handleOpenRestrictions(user.userId, user.firstName, user.lastName)}
                            size="small"
                          >
                            <LockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        color="primary"
                        onClick={() => handleViewUserDetails(user.userId)}
                      >
                        <ViewIcon />
                      </IconButton>
                      <Tooltip
                        title={!canManageUsers ? 'You do not have permission to edit users' : 'Edit user'}
                        disableHoverListener={canManageUsers}
                      >
                        <span style={{ display: 'inline-flex' }}>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              if (!canManageUsers) return;
                              handleEditUser(user.userId);
                            }}
                            disabled={!canManageUsers}
                            sx={{ cursor: !canManageUsers ? 'not-allowed' : 'pointer' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteUser(user.userId)}
                        disabled={!user.isActive}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsDialogOpen}
        onClose={() => setUserDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" component="span">
              User Details
            </Typography>
            <Chip
              label={selectedUserDetails?.isActive ? 'Active' : 'Inactive'}
              color={selectedUserDetails?.isActive ? 'success' : 'error'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedUserDetails ? (
            <Box sx={{ mt: 2 }}>
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Basic Information
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                        <Typography>{`${selectedUserDetails.firstName} ${selectedUserDetails.lastName}`}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Username</Typography>
                        <Typography>{selectedUserDetails.username}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                        <Typography>{selectedUserDetails.email}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                        <Typography>{selectedUserDetails.drivingLicense?.address || 'Not provided'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                        <Chip label={selectedUserDetails.role} color="primary" size="small" />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Organization Details
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Business</Typography>
                        <Typography>{selectedUserDetails.businessName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                        <Typography>{selectedUserDetails.locationName || 'Not assigned'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Last Login</Typography>
                        <Typography>
                          {selectedUserDetails.lastLoginAt 
                            ? new Date(selectedUserDetails.lastLoginAt).toLocaleString()
                            : 'Never'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Member Since</Typography>
                        <Typography>{new Date(selectedUserDetails.createdAt).toLocaleDateString()}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Teams
                </Typography>
                {selectedUserDetails.teams.length > 0 ? (
                  <Grid container spacing={2}>
                    {selectedUserDetails.teams.map((team) => (
                      <Grid item xs={12} key={team.teamId}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {team.teamName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {team.description}
                              </Typography>
                            </Box>
                            <Box textAlign={{ xs: 'left', sm: 'right' }}>
                              <Typography variant="subtitle2" color="text.secondary">Team Leaders</Typography>
                              {team.teamLeaders?.length ? (
                                team.teamLeaders.map((leader) => (
                                  <Box key={leader.userId} sx={{ mb: 0.5 }}>
                                    <Typography>
                                      {leader.firstName} {leader.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {leader.email}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <>
                                  <Typography>
                                    {team.teamLeaderFirstName && team.teamLeaderLastName
                                      ? `${team.teamLeaderFirstName} ${team.teamLeaderLastName}`
                                      : 'Not assigned'}
                                  </Typography>
                                  {team.teamLeaderEmail && (
                                    <Typography variant="body2" color="text.secondary">
                                      {team.teamLeaderEmail}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="text.secondary">No teams assigned</Typography>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Tags
                </Typography>
                {selectedUserDetails.jobTags.length > 0 ? (
                  <Grid container spacing={2}>
                    {selectedUserDetails.jobTags.map((tag) => (
                      <Grid item xs={12} sm={6} md={4} key={tag.jobTagId}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {tag.tagName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {tag.description}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Assigned: {new Date(tag.assignedAt).toLocaleDateString()}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="text.secondary">No tags assigned</Typography>
                )}
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button variant="contained" onClick={() => setUserDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          {inviteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {inviteSuccess}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={inviteFormData.email}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={inviteFormData.firstName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, firstName: e.target.value })}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={inviteFormData.lastName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, lastName: e.target.value })}
                  required
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteUser}>
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Invite Dialog */}
      <Dialog
        open={bulkInviteDialogOpen}
        onClose={() => setBulkInviteDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Bulk Invite Users</DialogTitle>
        <DialogContent>
          {bulkInviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkInviteError}
            </Alert>
          )}
          {bulkInviteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {bulkInviteSuccess}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />
            {loadingUsers ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.length === availableUsers.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers(availableUsers.map(user => user.userId));
                            } else {
                              setSelectedUsers([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedUsers.includes(user.userId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers([...selectedUsers, user.userId]);
                              } else {
                                setSelectedUsers(selectedUsers.filter((id) => id !== user.userId));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Tooltip title={user.lastInvitedAt ? `Last invited ${formatLastInvitedTime(user.lastInvitedAt)}` : 'Never invited'}>
                            <Chip
                              label="Available"
                              color="success"
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setBulkInviteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={fetchAvailableUsers}
            disabled={loadingUsers}
            sx={{ mr: 1 }}
          >
            Refresh Users
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkInviteSelected}
            disabled={selectedUsers.length === 0 || loadingBulkInvite}
          >
            {loadingBulkInvite ? 'Sending Invitations...' : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add BulkImportDialog */}
      <BulkImportDialog
        open={bulkImportDialogOpen}
        onClose={() => setBulkImportDialogOpen(false)}
        onSuccess={(message?: string) => {
          setBulkImportDialogOpen(false);
          fetchUsers();
          if (message) {
            setSuccessMessage(message);
            setTimeout(() => setSuccessMessage(null), 5000);
          }
        }}
        roles={roles}
        jobTags={jobTags}
        teams={teams}
      />

      {/* User Restrictions Dialog */}
      {restrictionsTarget && (
        <UserRestrictionsDialog
          open={restrictionsDialogOpen}
          onClose={() => { setRestrictionsDialogOpen(false); setRestrictionsTarget(null); }}
          userId={restrictionsTarget.userId}
          userName={restrictionsTarget.userName}
          canEdit={isAdminOrSuperAdmin}
          canEditLogin={
            currentUserRole === 'Super Admin' ||
            currentUserRole === 'Owner'
          }
        />
      )}
    </Box>
  );
};

export default UserManagement; 